import { tokenStorage } from './tokenStorage';

/**
 * Token Refresh Utility
 * Handles refreshing user access tokens using refresh tokens
 */

// Track if a refresh is in progress to prevent multiple concurrent refreshes
let refreshPromise = null;

/**
 * Refresh the access token using the stored refresh token
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
export async function refreshAccessToken() {
    // If a refresh is already in progress, wait for it
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = performRefresh();

    try {
        const result = await refreshPromise;
        return result;
    } finally {
        refreshPromise = null;
    }
}

/**
 * Internal function to perform the actual token refresh
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
async function performRefresh() {
    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) {
        return null;
    }

    const clientId = process.env.REACT_APP_AUTH_CLIENT_ID;
    const authIssuer = process.env.REACT_APP_AUTH_ISSUER;

    // Validate required environment variables
    if (!clientId || !authIssuer) {
        return null;
    }

    const refreshUrl = `${authIssuer}/protocol/openid-connect/token`;

    const params = new URLSearchParams();
    params.append('client_id', clientId);
    params.append('grant_type', 'refresh_token');
    params.append('refresh_token', refreshToken);

    try {
        const response = await fetch(refreshUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: params.toString()
        });

        if (!response.ok) {
            let errorDetails = '';
            try {
                const error = await response.json();
                errorDetails = JSON.stringify(error);
            } catch {
                errorDetails = await response.text();
            }

            // Clear tokens on refresh failure (refresh token may be expired or invalid)
            tokenStorage.clearTokens();
            return null;
        }

        const refreshed = await response.json();

        // Update stored tokens
        const expiresAt = Math.floor(Date.now() / 1000) + (refreshed.expires_in || 3600);
        tokenStorage.setTokens({
            access_token: refreshed.access_token,
            refresh_token: refreshed.refresh_token || refreshToken,
            id_token: refreshed.id_token || tokenStorage.getIdToken() || '',
            expires_at: expiresAt
        });

        return refreshed.access_token;
    } catch (error) {
        console.error('Error refreshing access token:', error);
        return null;
    }
}

/**
 * Get a valid access token, refreshing if necessary
 * @returns {Promise<string|null>} Valid access token or null if user not authenticated
 */
export async function getValidAccessToken() {
    // Check if we have a token
    let accessToken = tokenStorage.getAccessToken();

    if (!accessToken) {
        const refreshToken = tokenStorage.getRefreshToken();
        if (refreshToken) {
            accessToken = await refreshAccessToken();
        }
        return accessToken; // Will be null if no refresh token or refresh failed
    }

    // Check if token is expired or expiring soon (within 60 seconds)
    if (tokenStorage.isTokenExpired()) {
        // Try to refresh
        accessToken = await refreshAccessToken();
    } else {
        // Check if expiring soon
        const expiresAt = tokenStorage.getExpiresAt();
        if (expiresAt) {
            const now = Math.floor(Date.now() / 1000);
            if (expiresAt - now < 60) {
                // Token expiring soon, refresh proactively
                accessToken = await refreshAccessToken();
            }
        }
    }

    return accessToken;
}
